import {Component,OnDestroy,OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {ActivatedRoute,Router} from "@angular/router";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {Subscription} from "rxjs";
import {watchRouteParams} from "@share/utils/watch-route-params";

/**
 * Enum pour les noms des différents onglets de la page notifications
 */
export enum Onglets {
    MESSAGES_NOTIFICATION = "Notifications",
    BALISES_PERSONNALISEES = "Balises",
    RELANCES_AUTOMATIQUES = "Relances",
    MESSAGES_ACCUEIL = "Accueil",
    ENVOI_MAILS = "Mails",
}

/**
 * Sous-menu notifications
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    host: {'data-test-id': 'bibliotheque-notifications'},
    templateUrl: './bibliotheque-notifications.component.html'
})
export class BibliothequeNotificationsComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.MESSAGES_NOTIFICATION,
            libelle: 'admin.bibliotheque.notifications.onglets.notifications',
            url: 'Admin/Bibliotheque/Notification/Notifications'
        }, {
            code: Onglets.BALISES_PERSONNALISEES,
            libelle: 'admin.bibliotheque.notifications.onglets.balises',
            url: 'Admin/Bibliotheque/Notification/Balises'
        }, {
            code: Onglets.RELANCES_AUTOMATIQUES,
            libelle: 'admin.bibliotheque.notifications.onglets.relances',
            url: 'Admin/Bibliotheque/Notification/Relances'
        }, {
            code: Onglets.MESSAGES_ACCUEIL,
            libelle: 'admin.bibliotheque.notifications.onglets.accueil',
            url: 'Admin/Bibliotheque/Notification/Accueil'
        }, {
            code: Onglets.ENVOI_MAILS,
            libelle: 'admin.bibliotheque.notifications.onglets.mails',
            url: 'Admin/Bibliotheque/Notification/Mails'
        }
    ];

    /** Liste des différents onglets disponibles dans le menu notification */
    listeTabItems: Array<PageHeaderItem> = BibliothequeNotificationsComponent.listeOnglets;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /** Référence vers l'enum des onglets entreprise pour l'utiliser dans le template */
    Onglet = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number;

    /** Indique si le header doit être affiché */
    isDisplayed: boolean = false;

    /** Session */
    private session: Session;

    /** Souscription */
    private subscription: Subscription;

    /** Indique si on affiche le détail du message */
    get isInDetail(): boolean {
        const child = this.activatedRoute.snapshot.firstChild;
        const path = child?.routeConfig?.path;
        return !!path && path?.startsWith('Message/');
    }

    constructor(
        protected translateService: TranslateService,
        private router: Router,
        private store: Store<AppState>,
        private activatedRoute: ActivatedRoute,
    ) { }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de la session
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
            //Stockage de la session
            this.session = session;

            //Filtrage des onglets autorisés
            this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

            //Abonnement aux changements de route
            this.subscription = watchRouteParams(this.router,this.activatedRoute).subscribe(() => {
                //Récupération de la route
                const firstChild = this.activatedRoute.snapshot.firstChild;

                //Indique s'il faut afficher le header
                this.isDisplayed = (Object.values(firstChild.params).findIndex(v => v !== undefined) === -1);

                //Récupération du path
                const segment = firstChild.url[0].path;

                //On vérifie si on trouve l'onglet demandé via l'url
                const matching = Object.values(Onglets).find(v => v === segment);

                if (!!matching) {
                    //Si on trouve bien l'onglet correspondant, on met à jour le composant header
                    this.selectedItem = this.listeTabItems.find(i => i.code === segment);
                    this.selectedIndex = this.listeTabItems.findIndex(i => i.code === segment);
                } else {
                    //Sinon, on redirige sur le premier onglet et on met à jour le composant header
                    this.selectedItem = this.listeTabItems[0];
                    this.selectedIndex = 0;
                }
            });
        });
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
            this.router.navigateByUrl(selectedItem.url);
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation de l'abonnement
        this.subscription.unsubscribe();
    }
}
